import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Progress, Space, Spin, Tooltip, Typography } from 'antd';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useAuthStore } from '../../stores/authStore';
import { fetchMyQuota, type QuotaData } from '../../services/adminService';

const { Text } = Typography;

export const CANVAS_QUOTA_REFRESH_INTERVAL_MS = 20_000;

export type QuotaStatus = 'normal' | 'warning' | 'exception';

export interface QuotaPresentation {
  used: number;
  limit: number | null;
  percent: number | null;
  status: QuotaStatus;
  hint: string | null;
}

export function getQuotaPresentation(quota: QuotaData): QuotaPresentation | null {
  const used = Number(quota.used);
  if (!Number.isFinite(used) || used < 0) return null;
  const limit = quota.limit === null ? null : Number(quota.limit);
  if (limit !== null && (!Number.isFinite(limit) || limit < 0)) return null;
  if (limit === null) {
    return { used, limit: null, percent: null, status: 'normal', hint: null };
  }

  const ratio = limit === 0 ? 1 : used / limit;
  const percent = Math.min(100, Math.max(0, ratio * 100));
  const status: QuotaStatus = ratio >= 1 ? 'exception' : ratio >= 0.8 ? 'warning' : 'normal';
  const hint = ratio >= 1 ? '今日额度已用尽' : ratio >= 0.8 ? '今日额度即将用尽' : null;
  return { used, limit, percent, status, hint };
}

export interface CanvasQuotaProps {
  /** Kept injectable for tests and for callers that already have a quota loader. */
  quotaLoader?: () => Promise<QuotaData>;
  role?: string;
}

export function shouldLoadCanvasQuota(role: string | undefined): boolean {
  return Boolean(role) && role !== 'ADMIN' && role !== 'SUPER_ADMIN';
}

const defaultQuotaLoader = () => fetchMyQuota();

const CanvasQuota: React.FC<CanvasQuotaProps> = ({ quotaLoader = defaultQuotaLoader, role }) => {
  const authRole = useAuthStore((state) => state.user?.role);
  const effectiveRole = role ?? authRole;
  const [quota, setQuota] = useState<QuotaPresentation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const loadQuota = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = getQuotaPresentation(await quotaLoader());
      if (!result) throw new Error('Invalid quota response');
      setQuota(result);
    } catch {
      setQuota(null);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [quotaLoader]);

  useEffect(() => {
    if (!shouldLoadCanvasQuota(effectiveRole)) return;
    void loadQuota();
    const refreshTimer = window.setInterval(() => void loadQuota(), CANVAS_QUOTA_REFRESH_INTERVAL_MS);
    const refreshOnFocus = () => void loadQuota();
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      window.clearInterval(refreshTimer);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [effectiveRole, loadQuota]);

  const content = useMemo(() => {
    if (loading) return <Spin size="small" />;
    if (error) {
      return (
        <Tooltip title="额度加载失败，点击重试">
          <Button type="text" size="small" icon={<ReloadOutlined />} onClick={() => void loadQuota()} />
        </Tooltip>
      );
    }
    if (!quota) return null;
    if (quota.limit === null) {
      return <Text type="secondary" style={{ fontSize: 12 }}>今日已用 ¥{quota.used.toFixed(2)} · 不限额</Text>;
    }
    return (
      <Space size={8} style={{ minWidth: 220 }}>
        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
          今日额度 ¥{quota.used.toFixed(2)} / ¥{quota.limit.toFixed(2)}
        </Text>
        <Progress
          percent={quota.percent ?? 0}
          status={quota.status === 'exception' ? 'exception' : 'normal'}
          strokeColor={quota.status === 'warning' ? '#faad14' : undefined}
          showInfo={false}
          size="small"
          style={{ width: 74, margin: 0 }}
        />
        {quota.hint && (
          <Tooltip title={quota.hint}>
            <WarningOutlined style={{ color: quota.status === 'exception' ? '#ff4d4f' : '#faad14' }} />
          </Tooltip>
        )}
      </Space>
    );
  }, [error, loadQuota, loading, quota]);

  if (!shouldLoadCanvasQuota(effectiveRole)) return null;
  return <div data-testid="canvas-quota" style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>{content}</div>;
};

export default CanvasQuota;
